import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageCircle, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Community({ user, onLogout }) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const res = await axios.get(`${API}/communities`, { headers: getAuthHeaders() });
      setCommunities(res.data);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="community">
        <div className="p-8">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="community">
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy">Course Communities</h1>
          <p className="text-[#4D4D4D] mt-2 font-light">Join discussions, ask doubts, and connect with your peers</p>
        </div>

        {communities.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-12 text-center">
              <MessageCircle className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Communities Yet</h3>
              <p className="text-[#4D4D4D] font-light">Teachers will publish course-specific discussions here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {communities.map((community) => (
              <Card 
                key={community.id} 
                className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth overflow-hidden cursor-pointer"
                onClick={() => navigate(`/community/${community.id}`)}
              >
                <div className="md:flex">
                  {/* Hero Image */}
                  {community.hero_image_url && (
                    <div className="md:w-80 h-48 md:h-auto overflow-hidden flex-shrink-0">
                      <img 
                        src={`${BACKEND_URL}${community.hero_image_url}`} 
                        alt={community.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <CardContent className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-navy mb-2">{community.title}</h2>
                        <p className="text-sm text-primary-blue font-medium mb-3">{community.course_title}</p>
                      </div>
                    </div>
                    
                    {/* Teaser Points */}
                    <div className="space-y-2 mb-4">
                      {community.short_description.split('\n').slice(0, 4).map((line, idx) => (
                        line.trim() && (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary-blue mt-2 flex-shrink-0"></div>
                            <p className="text-[#4D4D4D] text-sm">{line.trim()}</p>
                          </div>
                        )
                      ))}
                    </div>
                    
                    {/* Tags */}
                    {community.tags && community.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {community.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-blue-50 text-primary-blue text-xs rounded-md font-medium">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Meta Info */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center space-x-4 text-sm text-[#A5A5A5]">
                        <div className="flex items-center space-x-1">
                          <MessageCircle className="w-4 h-4" strokeWidth={2} />
                          <span>{community.post_count || 0} posts</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users className="w-4 h-4" strokeWidth={2} />
                          <span>By {community.creator_name}</span>
                        </div>
                      </div>
                      
                      <Button
                        className="bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/community/${community.id}`);
                        }}
                      >
                        Start Discussion
                        <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}